import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

/**
 * One entry in `blockersJson` — a reason the convergence agent gave up on a
 * node instead of promoting it. Mirrors `ConvergenceBlocker` on the API side.
 */
export type ConvergenceBlocker = {
  kind: string;
  detail: string;
  nodeId?: string | null;
  proposal?: string | null;
};

/**
 * `reportJson` is a loose blob the agent accretes across phases
 * (`preflight → fork → converge → verify → promote → origin_gate → finalize`).
 * Every field is optional — a run that died in preflight has almost none of
 * them — so treat this as "may contain" rather than a guaranteed shape.
 */
export type ConvergenceReportJson = {
  forkProofRunIds?: string[];
  originGateRunIds?: string[];
  error?: string;
  abortReason?: string;
  /**
   * Everything else the agent accretes (node map, config edits, decisions,
   * promoted nodes, origin-gate evidence). Deliberately left untyped here:
   * the drawer renders those through the server-rendered markdown report, so
   * duplicating their shapes client-side would be a second source of truth
   * that silently drifts. `promotedNodes[].persistMode`, for instance, is
   * `string | { mode, cause? }` — mistyping it as a string is exactly how
   * `[object Object]` reaches a user.
   */
  [key: string]: unknown;
};

/** Why a run is stuck, labelled API-side. Null for Cofactor-owned reasons and old rows. */
export type ConvergenceRunStuck = {
  label: string;
  remediation: string | null;
};

export type ConvergenceRunDetail = {
  convergenceRunId: string;
  workflowId: string;
  forkWorkflowId: string | null;
  status: string;
  phase: string;
  outcome: string | null;
  cycleCount: number;
  cycleCap: number | null;
  cacheOnlyRunCount?: number | null;
  jobId: string | null;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  orgId: string;
  triggeredByUserId: string | null;
  modelId: string | null;
  requestedInstructions: string | null;
  reportJson: ConvergenceReportJson | null;
  blockersJson: ConvergenceBlocker[] | null;
  reportGeneratedAt: string | null;
  stuck: ConvergenceRunStuck | null;
};

type ConvergenceRunDetailResponse = { ok: boolean; data: ConvergenceRunDetail };

export async function fetchConvergenceRun(id: string): Promise<ConvergenceRunDetail> {
  const response = await fetch(`${API_URL}/convergence-runs/${id}`, { credentials: 'include' });
  if (!response.ok) {
    throw await toApiError(response, 'Failed to load convergence run');
  }
  const body = (await response.json()) as ConvergenceRunDetailResponse;
  return body.data;
}

/**
 * Fetches optimization run detail. The server scopes this read to the
 * workflow's organization, so any member who can see the flow can inspect it.
 */
export function useConvergenceRun(id: string | null, options?: { enabled?: boolean }) {
  return useQuery<ConvergenceRunDetail>({
    queryKey: ['convergence-run', id],
    enabled: Boolean(id) && (options?.enabled ?? true),
    queryFn: () => fetchConvergenceRun(id as string),
  });
}

export type ConvergenceRunReport = {
  runId: string;
  markdown: string;
  cached: boolean;
};

type ConvergenceRunReportResponse = { ok: boolean; data: ConvergenceRunReport };

export async function fetchConvergenceRunReport(id: string): Promise<ConvergenceRunReport> {
  const url = new URL(`${API_URL}/convergence-runs/${id}/report`);
  url.searchParams.set('format', 'markdown');
  const response = await fetch(url.toString(), { credentials: 'include' });
  if (!response.ok) {
    throw await toApiError(response, 'Failed to load convergence run report');
  }
  const body = (await response.json()) as ConvergenceRunReportResponse;
  return body.data;
}

/**
 * Fetches the rendered markdown report for an optimization run. Rendering the
 * report server-side is real work, so callers should only enable this once
 * the run is actually being viewed (e.g. the drawer is open) rather than
 * prefetching it alongside the run list.
 */
export function useConvergenceRunReport(id: string | null, options?: { enabled?: boolean }) {
  return useQuery<ConvergenceRunReport>({
    queryKey: ['convergence-run-report', id],
    enabled: Boolean(id) && (options?.enabled ?? true),
    queryFn: () => fetchConvergenceRunReport(id as string),
  });
}

/**
 * POST /convergence-runs/:id/resolve-review — drops a pending review (the run
 * outcome becomes `unverified`) without merging the fork, so automatic
 * maintenance of the flow can resume. Expected failures are typed user errors
 * (`CONVERGENCE_REVIEW_NOT_PENDING`, `CONVERGENCE_REVIEW_ALREADY_RESOLVED`).
 */
export function useResolveConvergenceReview(convergenceRunId: string, workflowId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string | undefined) => {
      const r = await fetch(`${API_URL}/convergence-runs/${convergenceRunId}/resolve-review`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(reason ? { reason } : {}),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to resolve the review');
      return r.json() as Promise<{ ok: true; data: { convergenceRunId: string } }>;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['convergence-run', convergenceRunId] });
      void qc.invalidateQueries({ queryKey: ['convergence-run-report', convergenceRunId] });
      if (!workflowId) return;
      void qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      void qc.invalidateQueries({ queryKey: ['optimization-runs', workflowId] });
      void qc.invalidateQueries({ queryKey: ['workflow-runs', workflowId] });
    },
  });
}
